// React hooks for environmental risk factors
import React, { useState, useEffect } from 'react';
import { environmentalRiskFactorService } from '../services/environmentalRiskFactorService';

/**
 * Hook to get all environmental risk factors with optional filters
 */
const useEnvironmentalRiskFactors = (filters = {}) => {
  const [factors, setFactors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const loadFactors = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let data;
      
      if (filters.category) {
        data = await environmentalRiskFactorService.getFactorsByCategory(filters.category);
      } else if (filters.departmentId) {
        data = await environmentalRiskFactorService.getFactorsByDepartment(filters.departmentId);
      } else if (filters.seasonal) {
        data = await environmentalRiskFactorService.getSeasonalFactors();
      } else if (filters.highImpact) {
        data = await environmentalRiskFactorService.getHighImpactFactors();
      } else if (filters.context) {
        data = await environmentalRiskFactorService.getApplicableFactors(filters.context);
      } else if (filters.activeOnly === false) {
        data = await environmentalRiskFactorService.getAllFactors();
      } else {
        // Default to active factors only
        data = await environmentalRiskFactorService.getActiveFactors();
      }
      
      setFactors(data);
    } catch (err) {
      console.error('Error loading environmental risk factors:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadFactors();
  }, [
    filters.category,
    filters.departmentId,
    filters.seasonal,
    filters.highImpact,
    filters.activeOnly,
    JSON.stringify(filters.context) // Serialize context object for comparison
  ]);
  
  const refresh = () => {
    loadFactors();
  };
  
  return { factors, loading, error, refresh };
};

/**
 * Hook to get a single environmental risk factor by ID
 */
const useEnvironmentalRiskFactor = (factorId) => {
  const [factor, setFactor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const loadFactor = async () => {
    if (!factorId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const data = await environmentalRiskFactorService.getFactor(factorId);
      setFactor(data);
    } catch (err) {
      console.error('Error loading environmental risk factor:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadFactor();
  }, [factorId]);
  
  const refresh = () => {
    loadFactor();
  };
  
  return { factor, loading, error, refresh };
};

/**
 * Hook for environmental risk factor mutations
 */
const useEnvironmentalRiskFactorMutations = () => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  const createFactor = async (factorData, userId) => {
    try {
      setSaving(true);
      setError(null);
      const factorId = await environmentalRiskFactorService.createFactor(factorData, userId);
      return factorId;
    } catch (err) {
      console.error('Error creating environmental risk factor:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };
  
  const updateFactor = async (factorId, updates, userId) => {
    try {
      setSaving(true);
      setError(null);
      await environmentalRiskFactorService.updateFactor(factorId, updates, userId);
    } catch (err) {
      console.error('Error updating environmental risk factor:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };
  
  const deleteFactor = async (factorId) => {
    try {
      setSaving(true);
      setError(null);
      await environmentalRiskFactorService.deleteFactor(factorId);
    } catch (err) {
      console.error('Error deleting environmental risk factor:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };
  
  const toggleActive = async (factorId, userId) => {
    try {
      setSaving(true);
      setError(null);
      await environmentalRiskFactorService.toggleActive(factorId, userId);
    } catch (err) {
      console.error('Error toggling environmental risk factor:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };
  
  return {
    createFactor,
    updateFactor,
    deleteFactor,
    toggleActive,
    saving,
    error
  };
};

/**
 * Hook to get environmental risk factor statistics
 */
const useEnvironmentalRiskFactorStatistics = () => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const loadStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await environmentalRiskFactorService.getFactorStatistics();
      setStatistics(data);
    } catch (err) {
      console.error('Error loading environmental risk factor statistics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadStatistics();
  }, []);
  
  const refresh = () => {
    loadStatistics();
  };
  
  return { statistics, loading, error, refresh };
};

/**
 * Hook to calculate total multiplier for a set of factor IDs
 */
const useEnvironmentalRiskMultiplier = (factorIds) => {
  const [multiplier, setMultiplier] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const calculateMultiplier = async () => {
    try {
      setLoading(true);
      setError(null);
      const total = await environmentalRiskFactorService.calculateTotalMultiplier(factorIds);
      setMultiplier(total);
    } catch (err) {
      console.error('Error calculating environmental risk multiplier:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (factorIds && factorIds.length > 0) {
      calculateMultiplier();
    } else {
      setMultiplier(1.0);
      setLoading(false);
    }
  }, [JSON.stringify(factorIds)]); // Use JSON.stringify for array comparison
  
  return { multiplier, loading, error };
};

export {
  useEnvironmentalRiskFactors,
  useEnvironmentalRiskFactor,
  useEnvironmentalRiskFactorMutations,
  useEnvironmentalRiskFactorStatistics,
  useEnvironmentalRiskMultiplier
};

